import * as Sentry from "@sentry/react";

// Type definitions for anonymous visitor
export interface AnonymousVisitor {
  id?: string;
  visited_at?: string;
  started_form?: boolean;
  started_form_at?: string;
  completed_form?: boolean;
  completed_form_at?: string;
  // Associate the visitor with a specific job where applicable
  job_id?: string;
  // Source tracking for marketing attribution
  source?: string;
  // Additional fields for analytics (not in DB schema but useful for tracking)
  device_info?: {
    browser?: string;
    os?: string;
    device?: string;
    screen_resolution?: string;
  };
}

// Type for visitor update data
export interface VisitorUpdateData {
  id: string;
  started_form?: boolean;
  started_form_at?: string;
  completed_form?: boolean;
  completed_form_at?: string;
  // Allow adding/updating job association on updates too
  job_id?: string;
  device_info?: {
    browser?: string;
    os?: string;
    device?: string;
    screen_resolution?: string;
  };
}

/**
 * Create a new anonymous visitor via cloud function
 */
export const createAnonymousVisitor = async (
  visitorData: Omit<AnonymousVisitor, "id" | "created_at" | "updated_at">
): Promise<{
  success: boolean;
  message: string;
  visitor?: AnonymousVisitor;
}> => {
  try {
    // Get the function URL from environment variables
    const functionUrl = `${
      import.meta.env.VITE_SUPABASE_URL
    }/functions/v1/create-anonymous-visitor`;

    // Get the anon key for authorization
    const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

    if (!functionUrl || !anonKey) {
      throw new Error("Missing Supabase configuration");
    }

    // Call the cloud function
    const response = await fetch(functionUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${anonKey}`,
      },
      body: JSON.stringify(visitorData),
    });

    // Parse the response
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || "Failed to create anonymous visitor");
    }

    return {
      success: true,
      message: "Anonymous visitor created successfully",
      visitor: data.visitor,
    };
  } catch (error) {
    console.error("Error in createAnonymousVisitor:", error);

    Sentry.captureException(error, {
      extra: {
        context: "anonymousVisitorService.createAnonymousVisitor",
        visitorData: JSON.stringify(visitorData),
      },
    });

    return {
      success: false,
      message:
        error instanceof Error
          ? error.message
          : "Failed to create anonymous visitor",
    };
  }
};

/**
 * Update an existing anonymous visitor via cloud function
 */
export const updateAnonymousVisitor = async (
  updateData: VisitorUpdateData
): Promise<{
  success: boolean;
  message: string;
  visitor?: AnonymousVisitor;
}> => {
  try {
    // Get the function URL from environment variables
    const functionUrl = `${
      import.meta.env.VITE_SUPABASE_URL
    }/functions/v1/update-anonymous-visitor`;

    // Get the anon key for authorization
    const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

    if (!functionUrl || !anonKey) {
      throw new Error("Missing Supabase configuration");
    }

    // Call the cloud function
    const response = await fetch(functionUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${anonKey}`,
      },
      body: JSON.stringify(updateData),
    });

    // Parse the response
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || "Failed to update anonymous visitor");
    }

    return {
      success: true,
      message: "Anonymous visitor updated successfully",
      visitor: data.visitor,
    };
  } catch (error) {
    console.error("Error in updateAnonymousVisitor:", error);

    Sentry.captureException(error, {
      extra: {
        context: "anonymousVisitorService.updateAnonymousVisitor",
        visitorId: updateData.id,
      },
    });

    return {
      success: false,
      message:
        error instanceof Error
          ? error.message
          : "Failed to update anonymous visitor",
    };
  }
};
